import React, { useState } from 'react';
import {
  Stethoscope,
  Hotel,
  GraduationCap,
  HeartHandshake,
  Building2,
  Briefcase,
  Landmark,
  Home,
  ShoppingBag,
  Rocket,
  CheckCircle2,
  ArrowRight,
  TrendingUp
} from 'lucide-react';
import { INDUSTRIES, IndustryItem } from '../data/siteData';

interface IndustriesSectionProps {
  onOpenBooking: (industryName?: string) => void;
  onExploreWork: (industryName?: string) => void;
  isPageHeader?: boolean;
}

export const IndustriesSection: React.FC<IndustriesSectionProps> = ({ onOpenBooking, onExploreWork, isPageHeader }) => {
  const [selectedIndustryId, setSelectedIndustryId] = useState<string>('healthcare');
  const [showAllIndustries, setShowAllIndustries] = useState<boolean>(false);

  const selectedIndustry = INDUSTRIES.find((i) => i.id === selectedIndustryId) || INDUSTRIES[0];

  const getIndustryIcon = (iconName: string) => {
    switch (iconName) {
      case 'Stethoscope': return <Stethoscope className="h-5 w-5" />;
      case 'Hotel': return <Hotel className="h-5 w-5" />;
      case 'GraduationCap': return <GraduationCap className="h-5 w-5" />;
      case 'HeartHandshake': return <HeartHandshake className="h-5 w-5" />;
      case 'Building2': return <Building2 className="h-5 w-5" />;
      case 'Briefcase': return <Briefcase className="h-5 w-5" />;
      case 'Landmark': return <Landmark className="h-5 w-5" />;
      case 'Home': return <Home className="h-5 w-5" />;
      case 'ShoppingBag': return <ShoppingBag className="h-5 w-5" />;
      case 'Rocket': return <Rocket className="h-5 w-5" />;
      default: return <Briefcase className="h-5 w-5" />;
    }
  };

  return (
    <section id="industries" className="py-20 lg:py-28 bg-slate-50 border-b border-slate-200">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="max-w-3xl text-left mb-14">
          <div className="text-xs font-bold uppercase tracking-widest text-[#b88e4c] mb-2">
            Industries
          </div>
          {isPageHeader ? (
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-display">
              Vertical depth across <br className="hidden sm:block" />
              ten industries.
            </h1>
          ) : (
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-display">
              Vertical depth across <br className="hidden sm:block" />
              ten industries.
            </h2>
          )}
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed font-normal">
            We bring battle-tested playbooks to the businesses that move the African economy.
          </p>
        </div>

        {/* Industry Buttons Grid with View More Dropdown Toggle */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 mb-4">
          {(showAllIndustries ? INDUSTRIES : INDUSTRIES.slice(0, 5)).map((ind) => {
            const isSelected = ind.id === selectedIndustryId;
            return (
              <button
                key={ind.id}
                onClick={() => setSelectedIndustryId(ind.id)}
                className={`flex items-center gap-2.5 p-3 rounded-xl text-left text-xs font-semibold transition-all cursor-pointer border ${
                  isSelected
                    ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100/80 hover:text-slate-900'
                }`}
              >
                <span className={isSelected ? 'text-amber-400' : 'text-slate-400'}>
                  {getIndustryIcon(ind.iconName)}
                </span>
                <span className="truncate">{ind.name}</span>
              </button>
            );
          })}
        </div>

        {/* View More Industries Dropdown Bar */}
        <div className="mb-10 flex justify-center">
          <button
            onClick={() => setShowAllIndustries(!showAllIndustries)}
            className="px-4 py-2 rounded-xl bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 text-xs font-semibold flex items-center gap-2 cursor-pointer transition-colors shadow-xs"
          >
            <span>{showAllIndustries ? 'Collapse Industries (Show Top 5)' : 'View More Industries (5 More Verticals)'}</span>
            <span className="text-amber-600 font-bold">{showAllIndustries ? '▲' : '▼'}</span>
          </button>
        </div>

        {/* Selected Industry Card Spotlight */}
        <div className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-10 shadow-xs">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            <div className="lg:col-span-7 text-left space-y-4">
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#b88e4c] bg-emerald-50 px-3 py-1 rounded-full">
                <span>{selectedIndustry.subtitle}</span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-display">
                {selectedIndustry.name} Infrastructure Blueprint
              </h3>

              <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                {selectedIndustry.description}
              </p>

              <div className="pt-2">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
                  Tailored Systems Delivered:
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {selectedIndustry.solutionsProvided.map((sol) => (
                    <div key={sol} className="flex items-center gap-2 text-xs sm:text-sm text-slate-700 font-medium">
                      <CheckCircle2 className="h-4 w-4 text-[#b88e4c] shrink-0" />
                      <span>{sol}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-4 flex flex-wrap items-center gap-4">
                <button
                  onClick={() => onOpenBooking(selectedIndustry.name)}
                  className="rounded-xl bg-[#080d11] hover:bg-slate-800 text-white font-bold px-5 py-2.5 text-xs transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <span>Build {selectedIndustry.name} System</span>
                  <ArrowRight className="h-3.5 w-3.5 text-[#009fe3]" />
                </button>

                <button
                  onClick={() => onExploreWork(selectedIndustry.name)}
                  className="text-xs font-semibold text-[#b88e4c] hover:underline"
                >
                  View {selectedIndustry.name} case studies →
                </button>
              </div>
            </div>

            {/* Right Metric Callout */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-[#112337] text-white p-6 sm:p-8 text-left space-y-4 shadow-lg border border-slate-800">
                <div className="flex items-center gap-2 text-[#009fe3] text-xs font-mono">
                  <TrendingUp className="h-4 w-4" />
                  <span>MEASURED OUTCOME BENCHMARK</span>
                </div>
                <div className="text-2xl sm:text-3xl font-extrabold font-display text-white">
                  {selectedIndustry.sampleMetric}
                </div>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Based on real production data from deployed client infrastructure in East Africa over 12 months.
                </p>
                <div className="pt-4 border-t border-slate-800 text-xs text-slate-400 flex items-center justify-between">
                  <span>Audit &amp; deployment</span>
                  <span className="text-[#009fe3] font-bold">2 to 4 Weeks</span>
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};
